import { memo } from 'react';
import { useTheme } from 'styled-components';

import { useGetCounterpartiesQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import type { DealFixedRateLoanData } from '@src/types/OTC';
import { formatOtcDealDateTimeInTimezone } from '@src/utils/OTC/otcDealDisplayUtils';
import {
  Divider,
  EMPTY_OBJECT,
  HStack,
  InlineFormattedNumber,
  Text,
  VStack,
  isFixedRateLoanSecurity,
  toBig,
  useSecuritiesContext,
} from '@talos/kyoko';
import { Wrapper } from '../OMSCard/styles';
import { DealCardHeader } from './DealCardHeader';
import { DealCardRow } from './DealCardRow';
import { resolveDealSecurityForCard } from './resolveDealSecurityForCard';
import { useOtcDealSettlementTimezone } from './useOtcDealSettlementTimezone';

interface DealCardFixedRateLoanProps {
  deal: DealFixedRateLoanData;
}

export const DealCardFixedRateLoan = memo(function DealCardFixedRateLoan({ deal }: DealCardFixedRateLoanProps) {
  const theme = useTheme();
  const { subAccountsByName } = useSubAccounts();
  const { data: counterpartiesByName } = useGetCounterpartiesQuery(EMPTY_OBJECT);
  const { securitiesBySymbol } = useSecuritiesContext();
  const settlementTimezone = useOtcDealSettlementTimezone(deal);
  // Resolve fields for loan deals
  const side = deal.DealInfo?.Side;
  const loanAmount = deal.DealInfo?.LoanAmount;
  const maturityTime = deal.DealInfo?.ExpiryTime;
  const rateBig = toBig(deal.DealInfo?.Rate);
  const rate = rateBig ? rateBig.times(100).toFixed() : '-';
  const security = resolveDealSecurityForCard(securitiesBySymbol, deal);
  const borrowedAsset =
    (isFixedRateLoanSecurity(security) && security.ProductDetails.BorrowedAsset) || deal.DealInfo?.EnteredQuantityAsset;

  return (
    <Wrapper>
      <VStack gap="spacing8" alignItems="stretch" p="spacing4">
        {/* Header */}
        <DealCardHeader deal={deal} />

        <Divider color={theme.colors.gray['000']} />

        {/* Deal Details */}
        <VStack gap="spacing4" alignItems="stretch">
          <HStack justifyContent="space-between" alignItems="center">
            <Text size="fontSizeMd" weight="fontWeightMedium">
              {side || 'N/A'}
            </Text>
            <VStack gap="spacing2" alignItems="flex-end">
              <InlineFormattedNumber number={loanAmount} currency={borrowedAsset} />
            </VStack>
          </HStack>

          <DealCardRow label="Rate">
            <InlineFormattedNumber number={rate} currency="%" />
          </DealCardRow>

          <DealCardRow label="Counterparty">
            {deal.Counterparty
              ? (counterpartiesByName?.get(deal.Counterparty)?.DisplayName ?? deal.Counterparty)
              : deal.Counterparty}
          </DealCardRow>

          <DealCardRow label="Loan Sub Account">
            {deal.SubAccount
              ? (subAccountsByName.get(deal.SubAccount)?.DisplayName ?? deal.SubAccount)
              : deal.SubAccount}
          </DealCardRow>

          <DealCardRow label="Maturity Date">
            {formatOtcDealDateTimeInTimezone(maturityTime, settlementTimezone)}
          </DealCardRow>
        </VStack>
      </VStack>
    </Wrapper>
  );
});
